/**
 * User annotations on a recording (`app/services/annotation_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { publishAnnotationDelta } from '@brainfocus/web/analysis/annotation_broadcast';
import type { UserAnnotation } from '@brainfocus/web/domain/annotations/user_annotation';
import { assertPojo } from '@brainfocus/web/api/response';

// ========== User annotations (issues #365, #677) ==========
// Annotations are scoped to the UploadedEdf (the recording), not to a
// single analysis, so both the Analysis viewer (via analysis.edfId) and
// the sEEG Preview viewer (via its own EDF id) address the same set.
// Every accepted write is announced to the other tabs of this browser
// (`analysis/annotation_broadcast.ts`, PR #973 re-review).

/**
 * List the requesting user's annotations on a recording (#677). Used by the
 * sEEG Preview viewer, which loads no analysis and so cannot rely on the
 * ``annotations`` array bundled into ``/api/get-analysis``.
 */
export async function listAnnotations(edfId: number): Promise<UserAnnotation[]> {
    const response = await client.request('GET', `/api/edfs/${edfId}/annotations`);
    return response as UserAnnotation[];
}

export async function createAnnotation(edfId: number, payload: UserAnnotationInput): Promise<UserAnnotation> {
    const response = await client.request('POST', `/api/edfs/${edfId}/annotations`, payload);
    assertPojo<UserAnnotation>(response);
    // Only once the server accepted it: the other tabs apply it as is.
    publishAnnotationDelta({ edfId, op: 'upsert', annotation: response });
    return response;
}

export async function updateAnnotation(edfId: number, annotationId: number, payload: UserAnnotationInput): Promise<UserAnnotation> {
    const response = await client.request('PUT', `/api/edfs/${edfId}/annotations/${annotationId}`, payload);
    assertPojo<UserAnnotation>(response);
    publishAnnotationDelta({ edfId, op: 'upsert', annotation: response });
    return response;
}

export async function deleteAnnotation(edfId: number, annotationId: number): Promise<void> {
    await client.request('DELETE', `/api/edfs/${edfId}/annotations/${annotationId}`);
    publishAnnotationDelta({ edfId, op: 'delete', annotationId });
}

export interface UserAnnotationInput {
    channelId?: number | null;
    timeStart?: number;
    timeEnd?: number | null;
    text?: string;
    color?: string;
}
